import { Skeleton } from '@mui/material';
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Title,
  Tooltip,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { memo, useMemo } from 'react';
import { Bar } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';

import { ICaseStatisticsAverageByYear } from '@bvi/api-interfaces/response/case';
import { Div, DataPopover } from '@bvi/common-components';

import { formatCurrency } from '../../lib';
import { i18nInstance } from '../../locales';

import { styles } from './styles';

const MIN_YEARS_TO_DISPLAY = 3;
const MAX_YEARS_WITH_VALUE_LABELS = 10;

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
);

interface IResultsAveragesByYearChartProperties {
  averagesByYear?: Array<ICaseStatisticsAverageByYear>;
  isLoading?: boolean;
}

const buildYearRangeLabel = (
  years: Array<number>,
  joinYears: (years: Array<number>) => string,
  rangeYears: (from: number, to: number) => string,
) => {
  if (years.length <= 6) {
    return joinYears(years);
  }

  return rangeYears(years[0], years.at(-1) as number);
};

export const ResultsAveragesByYearChart: React.FC<IResultsAveragesByYearChartProperties> =
  memo(({ averagesByYear = [], isLoading = false }) => {
    const { t } = useTranslation('', {
      i18n: i18nInstance,
      keyPrefix: 'results.averagesByYearChart',
    });

    const rows = useMemo(
      () => [...averagesByYear].sort((left, right) => left.year - right.year),
      [averagesByYear],
    );

    if (!isLoading && rows.length < MIN_YEARS_TO_DISPLAY) {
      return null;
    }

    const years = rows.map((row) => row.year);
    const yearRangeLabel = buildYearRangeLabel(
      years,
      (values) => t('yearList', { years: values.join(' · ') }),
      (from, to) => t('yearRange', { from, to }),
    );
    const chartTitle = yearRangeLabel
      ? t('titleWithYears', { years: yearRangeLabel })
      : t('title');
    const showValueLabels = rows.length <= MAX_YEARS_WITH_VALUE_LABELS;

    return (
      <Div sx={styles.wrapper}>
        <DataPopover title={chartTitle} style={{ fontSize: '17px' }}>
          <Div sx={styles.canvasWrapper}>
            {isLoading ? (
              <Skeleton sx={styles.canvasSkeleton} />
            ) : (
              <Bar
                plugins={[ChartDataLabels]}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  animation: false,
                  plugins: {
                    legend: {
                      display: false,
                    },
                    tooltip: {
                      callbacks: {
                        label: (context) => {
                          const value = context.parsed.y;
                          if (value == null) {
                            return '';
                          }

                          const count = rows[context.dataIndex]?.count ?? 0;

                          return t('tooltip', {
                            average: formatCurrency(value),
                            count,
                          });
                        },
                      },
                    },
                    datalabels: {
                      display: showValueLabels,
                      anchor: 'end',
                      align: 'top',
                      clamp: true,
                      color: '#595959',
                      font: {
                        family: 'Lato',
                        size: 11,
                        weight: 600,
                      },
                      formatter: (value: number) => formatCurrency(value),
                    },
                  },
                  scales: {
                    x: {
                      grid: {
                        display: false,
                      },
                      ticks: {
                        font: {
                          family: 'Lato',
                          size: 12,
                        },
                        color: '#595959',
                        maxRotation: rows.length > 12 ? 45 : 0,
                        minRotation: rows.length > 12 ? 45 : 0,
                      },
                    },
                    y: {
                      beginAtZero: true,
                      ticks: {
                        font: {
                          family: 'Lato',
                          size: 12,
                        },
                        color: '#595959',
                        callback: (value) => formatCurrency(Number(value)),
                      },
                      grid: {
                        color: '#E8E8E8',
                      },
                    },
                  },
                }}
                data={{
                  labels: years.map(String),
                  datasets: [
                    {
                      label: t('seriesLabel'),
                      data: rows.map((row) => row.average),
                      backgroundColor: '#FFEBC2',
                      borderColor: '#E5B045',
                      borderWidth: 1,
                      borderRadius: 4,
                      maxBarThickness: 48,
                    },
                  ],
                }}
              />
            )}
          </Div>
        </DataPopover>
      </Div>
    );
  });
